// src/utils/communitySeen.js
//
// "How many open questions have turned up since this creator last looked at
// the forum" — the number the Community tab badge is actually trying to say.
//
// It used to show needs_answer_count raw, which is a property of the FORUM,
// not of you: with ~40 unanswered posts sitting there at any time, the badge
// read "43" forever, including in the second after you'd read every one of
// them. A badge that never clears is a badge people stop seeing, and it takes
// the genuinely new ones down with it.
//
// So opening /community marks the current count as read, and the badge shows
// the difference from there. No backend change and no per-post read tracking:
// the count is the same for everybody, so a watermark on it is enough to
// answer "anything new?" — which is the only question a tab badge can ask.
//
// Stored per-uid because logging out and in as a different creator on the
// same device should not inherit someone else's watermark, and per-device
// because localStorage is all this has; reading the forum on a laptop leaves
// the phone's badge alone. That's the accepted cost of not adding a server
// round trip for a decoration.

const PREFIX = 'mintlink.community.seenNeedsAnswer';

// Signals a fresh watermark to anything already mounted — the tab bar does
// not remount when you navigate to /community inside the app shell, so
// without this the badge would keep its pre-visit number until the next
// foreground.
export const COMMUNITY_SEEN_EVENT = 'mintlink:community-seen';

const keyFor = (uid) => `${PREFIX}.${uid || 'anon'}`;

// Every read and write is wrapped: Safari private mode throws on
// localStorage access rather than returning null, and a badge is never worth
// taking the nav down for.
const read = (uid) => {
  try {
    const raw = window.localStorage.getItem(keyFor(uid));
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
};

const write = (uid, count) => {
  try {
    window.localStorage.setItem(keyFor(uid), String(Math.max(0, Math.floor(count) || 0)));
    return true;
  } catch {
    return false;
  }
};

export const getCommunitySeen = read;

// Called when the feed is actually on screen. Fires the event so a mounted
// tab bar drops its badge in the same tick rather than one navigation later.
export const markCommunitySeen = (uid, count) => {
  write(uid, count);
  try {
    window.dispatchEvent(new CustomEvent(COMMUNITY_SEEN_EVENT, { detail: { uid, count } }));
  } catch { /* older shells without CustomEvent — the write still landed */ }
};

// The badge number: what's arrived since the watermark.
//
// A total BELOW the watermark means people answered things while you were
// away. Left alone, that would bank credit — with a watermark of 43 and a
// total of 30, thirteen brand-new questions could land before the badge said
// anything. So the watermark follows the count down; it only ever means
// "everything up to here has been seen".
export const communityBadgeCount = (uid, total) => {
  const n = Math.max(0, Math.floor(Number(total)) || 0);
  const seen = read(uid);
  if (n <= seen) {
    if (n < seen) write(uid, n);
    return 0;
  }
  return n - seen;
};
